import React, { useState } from 'react';
import { PlantInfo } from '../types/PlantInfo';

interface AccordionSectionProps {
  title: string;
  content: string;
  isOpen: boolean;
  onToggle: () => void;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  content,
  isOpen,
  onToggle,
}) => {
  return (
    <div className="mb-2 border border-gray-300 rounded-md overflow-hidden">
      <button
        onClick={onToggle}
        className={`w-full px-3.5 py-3 flex justify-between items-center text-left text-sm font-semibold transition-colors ${
          isOpen ? 'bg-blue-50' : 'bg-gray-50 hover:bg-gray-100'
        }`}
      >
        <span className="text-gray-800">
          {title}
        </span>
        <span
          className={`text-xs transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        >
          ▼
        </span>
      </button>
      {isOpen && (
        <div className="px-3.5 py-3 bg-white text-sm leading-relaxed text-gray-600 whitespace-pre-line">
          {content}
        </div>
      )}
    </div>
  );
};

interface PlantPopupProps {
  plant: PlantInfo;
}

const PlantPopup: React.FC<PlantPopupProps> = ({ plant }) => {
  const [openSections, setOpenSections] = useState<Set<string>>(new Set());

  const toggleSection = (section: string) => {
    setOpenSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(section)) {
        newSet.delete(section);
      } else {
        newSet.add(section);
      }
      return newSet;
    });
  };

  return (
    <div className="font-sans max-w-[350px]">
      <img
        src={plant.image}
        alt={plant.name}
        className="w-full h-[150px] object-cover rounded-lg mb-3"
      />
      <h3 className="text-lg font-bold text-gray-800 mb-3">{plant.name}</h3>

      <div className="space-y-0">
        <AccordionSection
          title="History"
          content={plant.history}
          isOpen={openSections.has('history')}
          onToggle={() => toggleSection('history')}
        />
        <AccordionSection
          title="Habitat"
          content={plant.habitat}
          isOpen={openSections.has('habitat')}
          onToggle={() => toggleSection('habitat')}
        />
        <AccordionSection
          title="Preparation"
          content={plant.preparation}
          isOpen={openSections.has('preparation')}
          onToggle={() => toggleSection('preparation')}
        />
        <AccordionSection
          title="Safety & Nutrition"
          content={plant.safetyNutrition}
          isOpen={openSections.has('safety')}
          onToggle={() => toggleSection('safety')}
        />
        <AccordionSection
          title="Warnings"
          content={plant.warnings}
          isOpen={openSections.has('warnings')}
          onToggle={() => toggleSection('warnings')}
        />
      </div>
    </div>
  );
};

export default PlantPopup;
